/** Section 31 - the ground a battle is fought on.
 *
 * The classic's nine magic terrains are not terrain kinds: a terrain is a movement
 * cost and a tile set with a transition to every neighbour, and magic ground is
 * a wash over whatever lies beneath it. What it IS, is a battle modifier and an
 * arena painting, and both of those are content (`MagicGroundDef`).
 *
 * Everything the ground does reaches the battle as a modifier on every stack on
 * BOTH sides. That scope is not a convenience, it is the rule: Ley Plains
 * casts every spell at expert for whoever is casting, and a Hexed Soil that
 * silenced one commander and not the other would be a thing you manoeuvre onto
 * rather than a place. The two heroes' own kits are gathered per side by
 * `heroMagicModifiers`; the ground is installed once, here, and the symmetry is
 * by construction.
 *
 * The one asymmetry is between CREATURES, not between sides: Blessed Earth lifts
 * the living and weighs on the dead. That is `abilityModifiers`, keyed by a
 * creature ability rather than by a creature, so a content set that adds an
 * undead creature gets the rule for free.
 *
 * Nothing here names a ground. Add one to `magic_grounds.json` and it works.
 */

import type { ContentRegistry, MagicGroundDef } from "../content/registry.js";
import type { Modifier, StatKey } from "../modifiers.js";
import { asModifierSrcId } from "../ids.js";
import { MASTERY_STAT, STAT_SPELL_LEVEL_CAP, STAT_SPELL_LEVEL_FLOOR, schoolDamageStat } from "./stats.js";
import type { SpellSchool } from "../content/registry.js";

/**
 * An authored modifier in the battle's own vocabulary.
 *
 * Content is authored in the stat names artifacts and skills use - `mastery.
 * fire`, `schoolDamage.water`, `spellLevelCap` - because that is the vocabulary
 * the schema validates and a content author already knows. The battle kernel
 * keys the same facts differently (`magic.mastery.fire`), so the two are joined
 * here rather than by making every author learn the second spelling.
 *
 * A stat with no translation passes through untouched: `morale` and `luck` are
 * read off a stack under their own names (`stackFortune`), and so is anything
 * an ability modifier wants to reach.
 */
export function groundStatKey(stat: string): StatKey {
  if (stat.startsWith("mastery.")) {
    const school = stat.slice("mastery.".length) as SpellSchool;
    return MASTERY_STAT[school] ?? (stat as StatKey);
  }
  if (stat.startsWith("schoolDamage.")) return schoolDamageStat(stat.slice("schoolDamage.".length));
  if (stat === "spellLevelCap") return STAT_SPELL_LEVEL_CAP;
  if (stat === "spellLevelFloor") return STAT_SPELL_LEVEL_FLOOR;
  return stat as StatKey;
}

const translate = (def: MagicGroundDef, mods: readonly Modifier[]): Modifier[] =>
  mods.map((m, i) => ({
    ...m,
    source: asModifierSrcId(`ground.${def.id}.${String(i)}.${m.stat}`),
    stat: groundStatKey(m.stat),
    expires: { kind: "battle" } as const,
  }));

/** What this ground puts on every stack, whatever the creature is. */
export function groundModifiers(def: MagicGroundDef): readonly Modifier[] {
  return translate(def, def.modifiers);
}

/**
 * What this ground puts on one creature, on top of the list above.
 *
 * `present: false` is the living half of Blessed Earth: a rule about the creature
 * that does NOT carry the ability, which is the only way to say "the living"
 * without listing every creature that is alive.
 */
export function groundCreatureModifiers(
  content: ContentRegistry, def: MagicGroundDef, creature: string,
): readonly Modifier[] {
  const abilities = content.creatures.get(creature as never)?.abilities ?? [];
  const out: Modifier[] = [];
  for (const rule of def.abilityModifiers) {
    const has = abilities.some((a) => a.kind === rule.ability);
    if (has !== rule.present) continue;
    for (const m of translate(def, rule.modifiers)) out.push(m);
  }
  return out;
}
